import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import './PurpleTeam.css';

import purpleShieldImg from '../../assets/home/purpleshield.png';
import redteamImg from '../../assets/purple/redteam.png';
import blueTeamVid from '../../assets/movie/Blueteam.mov';
import howImg from '../../assets/how_it_works.png';
import howGif from '../../assets/movie/how_it_works.gif';
import purpleInvestigateImg from '../../assets/home/purple_investigate.png';
import scheduleImg from '../../assets/purple/schedule.png';

const PurpleTeam = () => {
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (location.hash) {
      const id = location.hash.replace('#', '');
      setTimeout(() => {
        const el = document.getElementById(id);
        if (el) {
          const title = el.querySelector('h2, .pt-cta-btn, p');
          const target = title || el;
          const nav = document.querySelector('.nav-container');
          const navH = nav ? nav.offsetHeight : 64;
          window.scrollTo({ top: target.getBoundingClientRect().top + window.scrollY - navH - 40, behavior: 'smooth' });
        }
      }, 100);
    } else {
      window.scrollTo(0, 0);
    }
  }, [location.hash]);

  const [canScrollUp, setCanScrollUp] = useState(false);
  const [canScrollDown, setCanScrollDown] = useState(true);

  const sectionIds = ['pt-hero', 'section-red-team', 'section-blue-team', 'section-how-it-works', 'section-investigate', 'section-purple-anim', 'section-pt-cta'];

  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.scrollY;
      const docHeight = document.documentElement.scrollHeight;
      const winHeight = window.innerHeight;
      setCanScrollUp(scrollTop > 10);
      setCanScrollDown(scrollTop + winHeight < docHeight - 10);
    };
    window.addEventListener('scroll', handleScroll);
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const getScrollTarget = (section) => {
    const title = section.querySelector('h2, .pt-cta-btn, p');
    const target = title || section;
    const nav = document.querySelector('.nav-container');
    const navH = nav ? nav.offsetHeight : 64;
    return target.getBoundingClientRect().top + window.scrollY - navH - 40;
  };

  const scrollToId = (id) => {
    const el = document.getElementById(id);
    if (el) {
      window.scrollTo({ top: getScrollTarget(el), behavior: 'smooth' });
    }
  };

  const scrollToSection = useCallback((direction) => {
    const currentScroll = window.scrollY;
    const nav = document.querySelector('.nav-container');
    const navH = nav ? nav.offsetHeight : 64;
    const positions = sectionIds.map(id => {
      const el = document.getElementById(id);
      if (!el) return null;
      const title = el.querySelector('h2, .pt-cta-btn, p');
      const target = title || el;
      return { top: target.getBoundingClientRect().top + window.scrollY, id, scrollTo: target.getBoundingClientRect().top + window.scrollY - navH - 40 };
    }).filter(Boolean);

    if (direction === 'down') {
      const next = positions.find(p => p.scrollTo > currentScroll + 10);
      if (next) window.scrollTo({ top: next.scrollTo, behavior: 'smooth' });
    } else {
      const prev = [...positions].reverse().find(p => p.scrollTo < currentScroll - 10);
      if (prev) window.scrollTo({ top: prev.scrollTo, behavior: 'smooth' });
    }
  }, []);

  return (
    <>
      <div className="scroll-float-wrap">
        {canScrollUp && (
          <button className="scroll-float-bubble scroll-bubble-dark" onClick={() => scrollToSection('up')} aria-label="Scroll up">
            <svg width="36" height="36" viewBox="0 0 24 24" fill="none"><polygon points="12,6 4,16 20,16" fill="currentColor"/></svg>
          </button>
        )}
        {canScrollDown && (
          <button className="scroll-float-bubble scroll-bubble-dark" onClick={() => scrollToSection('down')} aria-label="Scroll down">
            <svg width="36" height="36" viewBox="0 0 24 24" fill="none"><polygon points="12,18 4,8 20,8" fill="currentColor"/></svg>
          </button>
        )}
      </div>

      <section id="pt-hero" className="main-hero demo-hero">
        <div className="hero-gif-fade-left"></div>
        <div className="hero-gif-fade-right"></div>
        <img src={purpleShieldImg} alt="Purple Shield" className="pt-hero-shield" />
        <h1 className="hero-page-title">Purple Team</h1>
      </section>

      <section className="pt-content">
        <div className="pt-layout">
          <h2 className="pt-heading">Don't Stop At Red Team, Remediate with Purple Team</h2>
          <p className="pt-description">
            Bltz Purple Team provides continuous, agentic AI risk testing and remediation, combining offensive (red team) and defensive (blue team) AI agents to find and fix vulnerabilities before attackers do.
          </p>

          <div className="pt-cta" style={{ marginTop: 0, marginBottom: 20 }}>
            <button className="pt-cta-btn" onClick={() => { navigate('/book-demo'); window.scrollTo(0, 0); }}>
              Get a Live Demo &rarr;
            </button>
          </div>

          {/* 3 Feature Boxes */}
          <div className="pt-features">
            <div className="pt-feature-box">
              <div className="pt-feature-header">
                <h3 className="pt-feature-title">Agentic Red Teaming</h3>
              </div>
              <p className="pt-feature-desc">Relentless attacker agent finding domain-specific vulnerabilities in your AI systems.</p>
              <div className="pt-feature-arrow" onClick={() => scrollToId('section-red-team')}>
                <span>&#8595;</span>
              </div>
            </div>
            <div className="pt-feature-box">
              <div className="pt-feature-header">
                <h3 className="pt-feature-title">Agentic Blue Teaming</h3>
              </div>
              <p className="pt-feature-desc">Self-learning defender agent mitigating threats and remediating risks in real time.</p>
              <div className="pt-feature-arrow" onClick={() => scrollToId('section-blue-team')}>
                <span>&#8595;</span>
              </div>
            </div>
            <div className="pt-feature-box">
              <div className="pt-feature-header">
                <h3 className="pt-feature-title">Why Defensive AI Security?</h3>
              </div>
              <p className="pt-feature-desc">See how with the right agentic strategies, you can protect your AI systems effectively.</p>
              <div className="pt-feature-arrow" onClick={() => scrollToId('section-how-it-works')}>
                <span>&#8595;</span>
              </div>
            </div>
          </div>

          {/* Red Team Section */}
          <div id="section-red-team" className="pt-section pt-section-light">
            <h2 className="pt-section-title">Agentic Red Teaming</h2>
            <p className="pt-section-desc">
              Simulates real-world attacks, not static prompt tests. Executes multi-step adversarial attack chains and uncovers vulnerabilities generic tools miss — continuously testing your AI systems with an autonomous offensive agent.
            </p>
            <div className="pt-img-wrap">
              <img src={purpleInvestigateImg} alt="Investigation" className="pt-img" style={{ marginBottom: 24 }} onContextMenu={e => e.preventDefault()} draggable={false} />
            </div>
            <div className="pt-scan-types">
              <h3 className="pt-section-title">No More Pre-Staged Red Team Not Relevant to Your Targets</h3>
              <p className="pt-section-desc">Basic Scan for fundamental risk validation, and Agent Scan for dynamic, domain-specific targeted attack simulation using a cutting-edge offensive agent armed with highly skilled adversarial security techniques.</p>
            </div>
            <div className="pt-img-wrap">
              <img src={redteamImg} alt="Red Team" className="pt-img" onContextMenu={e => e.preventDefault()} draggable={false} />
            </div>
          </div>

          {/* Blue Team Section */}
          <div id="section-blue-team" className="pt-section">
            <h2 className="pt-section-title">Agentic Blue Teaming</h2>
            <p className="pt-blue-highlight">Save hundreds of manual red team result review and manual fix</p>
            <p className="pt-section-desc">
              Detects and blocks threats in real time. Automatically remediates discovered risks, removes manual fix cycles, and reduces cost — a self-learning defensive agent that evolves with emerging threats.
            </p>
            <div className="pt-img-wrap">
              <video src={blueTeamVid} className="pt-img" autoPlay loop muted playsInline onContextMenu={e => e.preventDefault()} />
            </div>
          </div>

          {/* How It Works Section */}
          <div id="section-how-it-works" className="pt-section pt-section-light">
            <h2 className="pt-section-title">How It Works</h2>
            <p className="pt-section-desc">
              Bltz Purple Team combines offensive and defensive AI agents in a closed-loop system. The red team agent attacks, the blue team agent defends and remediates — continuously strengthening your AI security posture with every cycle.
            </p>
            <div className="pt-cta" style={{ marginTop: 0, marginBottom: 24 }}>
              <button className="pt-cta-btn" onClick={() => { navigate('/book-demo'); window.scrollTo(0, 0); }}>
                Get a Live Demo &rarr;
              </button>
            </div>
            <div className="pt-img-wrap">
              <img src={howImg} alt="How It Works" className="pt-img pt-img-still" onContextMenu={e => e.preventDefault()} draggable={false} />
              <img src={howGif} alt="How It Works Animation" className="pt-img pt-img-gif" onContextMenu={e => e.preventDefault()} draggable={false} />
            </div>
          </div>

          {/* Investigation Section */}
          <div id="section-investigate" className="pt-section">
            <h2 className="pt-section-title">Investigation &amp; Analysis</h2>
            <p className="pt-section-desc">
              Deep investigation capabilities that trace attack paths, analyze threat patterns, and provide actionable intelligence — giving your security team complete visibility into how AI threats operate and how to stop them.
            </p>
            <div className="pt-img-wrap">
              <img src={purpleInvestigateImg} alt="Purple Team Investigation" className="pt-img" onContextMenu={e => e.preventDefault()} draggable={false} />
            </div>
          </div>

          {/* Automation Section */}
          <div id="section-purple-anim" className="pt-section pt-section-light">
            <h2 className="pt-section-title">Automation</h2>
            <p className="pt-section-desc">
              Red Team and Blue Team on autopilot. No more manual work to harden your AI applications — our agents work 24/7 to safeguard your AI applications with continuous, automated security testing and remediation.
            </p>
            <div className="pt-img-wrap">
              <img src={scheduleImg} alt="Automation Schedule" className="pt-img" onContextMenu={e => e.preventDefault()} draggable={false} />
            </div>
          </div>

          {/* CTA */}
          <div id="section-pt-cta" className="pt-cta">
            <button className="pt-cta-btn" onClick={() => { navigate('/book-demo'); window.scrollTo(0, 0); }}>
              See Purple Team in Action &rarr;
            </button>
          </div>

          <span className="pt-back-link" onClick={() => { navigate('/'); window.scrollTo(0, 0); }} style={{ cursor: 'pointer' }}>
            ← Back to Home
          </span>
        </div>
      </section>
    </>
  );
};

export default PurpleTeam;
